/**
 * Thin logger facade.
 *
 * In development: prints to console.
 * In production: currently a no-op, but ready to forward to Sentry/Datadog
 * once an error-reporting service is configured (see Phase 9).
 *
 * Never use `console.*` directly in app code — always go through here so
 * we can swap the sink in one place.
 */

import { env } from '@/env';

type Level = 'debug' | 'info' | 'warn' | 'error';

function emit(level: Level, message: string, meta?: unknown): void {
  if (env.IS_DEV) {
    // eslint-disable-next-line no-console
    (console[level] ?? console.log)(
      `[${level}] ${message}`,
      meta === undefined ? '' : meta
    );
    return;
  }

  // In production, forward warn/error to telemetry if configured.
  // (Hooked in later by `initTelemetry()`.)
  if ((level === 'warn' || level === 'error') && typeof window !== 'undefined') {
    const reporter = (window as unknown as { __cmmsReport?: (l: Level, m: string, meta?: unknown) => void }).__cmmsReport;
    reporter?.(level, message, meta);
  }
}

export const logger = {
  debug: (message: string, meta?: unknown) => emit('debug', message, meta),
  info: (message: string, meta?: unknown) => emit('info', message, meta),
  warn: (message: string, meta?: unknown) => emit('warn', message, meta),
  error: (message: string, meta?: unknown) => emit('error', message, meta),
};
